import { useEffect, useMemo, useRef, useState } from "react";
import { ASSISTANT_SETTINGS_TAB_FIELDS } from "src/pages/admin/GeneralSettingsConsts";
import * as api from "src/services/api";
import { deleteWidgetCookie } from "@utils";
import { WIDGET_TOKEN } from "@consts";
import { trackEvent, ANALYTICS_EVENTS } from "@analytics";
import { useDraft } from "src/queries/hooks/assistants";
import { FETCH_QUERY_KEYS } from "src/queries/consts";
import { queryClient } from "src/lib/react-query/queryClient";
import { FORMATTING_HANDLERS, getFieldValue } from "src/pages/admin/GeneralSettings.utils";

const useGeneralSettings = (setFeedbackConfig, initialRow = null, isAdminContext = true) => {
  const generalSettingsEndpoint = (assistantId) =>
    (isAdminContext
      ? `/admin/assistants/${assistantId}/settings/general`
      : `/assistants/${assistantId}/settings/general`);

  const [settingsInitialData, setSettingsInitialData] = useState(null);
  const [settingsCurrentData, setSettingsCurrentData] = useState(null);
  const [changedSettings, setChangedSettings] = useState({});
  const [isPublishDialogOpen, setIsPublishDialogOpen] = useState(false);
  const [errorTexts, setErrorTexts] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [assistantSettingsProps, setAssistantSettingsProps] = useState({});
  const [rowInfo, setRowInfo] = useState(null);
  const { data: draft } = useDraft({ accountId: rowInfo?.accountId, assistantId: rowInfo?.id });

  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (initialRow && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchSettingsByAssistantId(initialRow);
    }
  }, [initialRow]);

  useEffect(() => {
    const detectChanges = ({ tab, field, value }) => {
      const objectValue = tab ? settingsInitialData.settings[tab][field] : settingsInitialData[field];

      if (objectValue !== value) {
        setChangedSettings((prev) => {
          const newSettings = { ...prev };

          if (tab) {
            return {
              ...newSettings,
              settings: {
                ...newSettings.settings,
                [tab]: {
                  ...newSettings.settings?.[tab],
                  [field]: value
                }
              }
            };
          }
          return {
            ...newSettings,
            [field]: value
          };

        });
      } else {

        setChangedSettings((prev) => {
          const newSettings = { ...prev };

          if (tab) {
            delete newSettings?.settings?.[tab]?.[field];
          } else {
            delete newSettings?.[field];
          }

          if (tab && Object.keys(newSettings?.settings?.[tab] || {}).length === 0) {
            delete newSettings?.settings?.[tab];
          }
          return newSettings;
        });
      }
    };

    const props = Object.values(ASSISTANT_SETTINGS_TAB_FIELDS).reduce(
      (acc, field) => ({
        ...acc,
        [field]: {
          value: getFieldValue(field, settingsCurrentData),
          handleChange: value => {
            const normalizedValue = FORMATTING_HANDLERS[field] ? FORMATTING_HANDLERS[field](value) : value;
            detectChanges({ field, value: normalizedValue });
            setSettingsCurrentData(prev => ({ ...prev, [field]: value }));
          },
          errorText: errorTexts[field],
          setErrorText: value =>
            setErrorTexts(prev => ({ ...prev, [field]: value }))
        }
      }),
      {}
    );

    setAssistantSettingsProps(props);
  }, [settingsCurrentData, errorTexts, settingsInitialData]);

  const fetchSettingsByAssistantId = async (row) => {
    try {
      const settings = await api.get({ endpoint: generalSettingsEndpoint(row.id) });
      setSettingsInitialData(settings);
      setSettingsCurrentData(settings);
      setRowInfo(row);
    } catch (e) {
      console.error("Error occurred while fetching settings by assistant id", e);
    }
  };

  // For every key in the current data, we check if it's different than original
  // In general settings we iterate over the keys straight away
  // eslint-disable-next-line consistent-return
  const areSettingsChanged = useMemo(() => {
    if (settingsCurrentData) {
      return Object.keys(settingsCurrentData).some(
        key =>
          JSON.stringify(settingsInitialData[key])
          !== JSON.stringify(settingsCurrentData[key])
          && !(
            settingsInitialData[key] === undefined
            && settingsCurrentData[key] === ""
          )
      );
    }
  }, [settingsInitialData, settingsCurrentData]);

  const isTimezoneChanged = ASSISTANT_SETTINGS_TAB_FIELDS.agentTimezone in changedSettings;

  const publishSettings = async () => {
    try {
      trackEvent(ANALYTICS_EVENTS.GENERAL.PUBLISH_CHANGES, {
        isNewAssistant: draft?.isFirstDraft
      });
      setIsLoading(true);
      await api.put({
        endpoint: generalSettingsEndpoint(rowInfo.id),
        data: { settingsData: { ...settingsCurrentData, ...changedSettings } }
      },
      setFeedbackConfig(
        {
          show: true,
          message: "Your changes have been published and should be effective within a few minutes",
          severity: "success"
        }
      ));
      if (isTimezoneChanged) {
        queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.ASSISTANT] });
        queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.CLINIC_AVAILABILITY, rowInfo.id] });
      }
      setSettingsInitialData(settingsCurrentData);
      setChangedSettings({});
      return true;
    } catch (e) {
      console.error("Error occurred while publishing settings changes", e);
      setFeedbackConfig(
        {
          show: true,
          message: "An error occurred while publishing your changes. Please try again later",
          severity: "error"
        }
      );
      return false;
    } finally {
      setIsLoading(false);
      setIsPublishDialogOpen(false);
      deleteWidgetCookie(WIDGET_TOKEN);
    }
  };

  const clearAssistantSettings = () => {
    setSettingsCurrentData({});
    setSettingsInitialData({});
    setChangedSettings({});
    setErrorTexts({});
  };

  return {
    isPublishDialogOpen,
    setIsPublishDialogOpen,
    assistantSettingsProps,
    areSettingsChanged,
    isLoading,
    clearAssistantSettings,
    publishSettings,
    settingsInitialData,
    isTimezoneChanged
  };
};

export default useGeneralSettings;
